import { $api } from '~/utils/api'
import { logApiError } from '~/utils/logger'
import { buildQueryString } from '~/utils/queryParams'

interface PartnerFilters {
  page?: number
  limit?: number
  search?: string
  isActive?: boolean
  sortBy?: string
  sortOrder?: 'asc' | 'desc'
}

interface PartnerInput {
  name: string
  description?: string | null
  website?: string | null
  image_url?: string | null
  is_active?: boolean
  display_order?: number
}

export interface Partner {
  id: string
  name: string
  description: string | null
  website: string | null
  image_url: string | null
  is_active: boolean
  display_order: number
  created_at: string
  updated_at: string
  created_by: string | null
  updated_by: string | null
}

interface PartnerListResponse {
  data: Partner[]
  metadata: {
    total: number
    page: number
    limit: number
    totalPages: number
  }
}

export const usePartners = () => {
  const getPartners = async (filters: PartnerFilters = {}): Promise<PartnerListResponse> => {
    try {
      const queryString = buildQueryString(filters)

      return await $api<PartnerListResponse>(`/api/partners${queryString ? `?${queryString}` : ''}`)
    }
    catch (error) {
      logApiError('/api/partners', error, { filters })
      throw error
    }
  }

  const getPartner = async (id: string): Promise<Partner> => {
    try {
      return await $api<Partner>(`/api/partners/${id}`)
    }
    catch (error) {
      logApiError(`/api/partners/${id}`, error)
      throw error
    }
  }

  const createPartner = async (data: PartnerInput): Promise<Partner> => {
    try {
      return await $api<Partner>('/api/partners', {
        method: 'POST',
        body: data,
      })
    }
    catch (error) {
      logApiError('/api/partners', error, { data })
      throw error
    }
  }

  const updatePartner = async (id: string, data: Partial<PartnerInput>): Promise<Partner> => {
    try {
      return await $api<Partner>(`/api/partners/${id}`, {
        method: 'PATCH',
        body: data,
      })
    }
    catch (error) {
      logApiError(`/api/partners/${id}`, error, { data })
      throw error
    }
  }

  const deletePartner = async (id: string): Promise<{ success: boolean }> => {
    try {
      return await $api<{ success: boolean }>(`/api/partners/${id}`, {
        method: 'DELETE',
      })
    }
    catch (error) {
      logApiError(`/api/partners/${id}`, error)
      throw error
    }
  }

  return {
    getPartners,
    getPartner,
    createPartner,
    updatePartner,
    deletePartner,
  }
}
